import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { Asset } from '../types';
import { QuickAuditData } from '../types';

// API Client Configuration
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Chỉ redirect nếu không phải đang ở trang login
          if (window.location.pathname !== '/login') {
            localStorage.removeItem('authToken');
            window.location.href = '/login';
          }
          // Nếu đang ở trang login thì chỉ trả lỗi về cho component xử lý
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic methods
  async get<T>(url: string, params?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data);
    return response.data;
  }

  // Auth methods
  async login(credentials: { username: string; password: string }) {
    const response = await this.post<{ token: string; user: any }>('/auth/login', credentials);
    localStorage.setItem('authToken', response.token);
    // Gọi tiếp API lấy user và lưu vào localStorage
    try {
      const userInfo = await this.getCurrentUser();
      if (userInfo && userInfo.user) {
        localStorage.setItem('user', JSON.stringify(userInfo.user));
      }
    } catch (err) {
      // Nếu lỗi vẫn cho đăng nhập nhưng không lưu user
      console.error('Không lấy được thông tin user sau khi login:', err);
    }
    return response;
  }

  async register(userData: { username: string; password: string; role: string; company_id?: number }) {
    return await this.post('/auth/register', userData);
  }

  async logout() {
    localStorage.removeItem('authToken');
  }

  async getCurrentUser() {
    return await this.get<{ user: any }>('/auth/me');
  }

  // Asset methods
  async getAssets(page?: number, limit?: number, extraParams?: any) {
    const params: any = { ...extraParams };
    if (page) params.page = page;
    if (limit) params.limit = limit;
    return await this.get('/assets', params);
  }

  async getAssetsForExport(filters?: any) {
    return await this.get<Asset[]>('/assets/export/data', filters);
  }

  async getAsset(id: number): Promise<Asset> {
    return await this.get<Asset>(`/assets/${id}`);
  }

  async getAssetMaintenanceSchedules(assetId: number) {
    return await this.get(`/asset-maintenance-schedules/asset/${assetId}`);
  }

  async deleteAssetMaintenanceSchedule(scheduleId: number) {
    return await this.delete(`/asset-maintenance-schedules/${scheduleId}`);
  }

  async createAssetMaintenanceSchedule(scheduleData: any) {
    return await this.post('/asset-maintenance-schedules', scheduleData);
  }

  async createAsset(assetData: any) {
    return await this.post('/assets', assetData);
  }

  async updateAsset(id: number, assetData: any) {
    return await this.put(`/assets/${id}`, assetData);
  }

  async deleteAsset(id: number) {
    return await this.delete(`/assets/${id}`);
  }

  // Asset Work Record methods
  async getAssetWorkRecords(params?: any) {
    if (params && typeof params === 'object') {
      const queryString = new URLSearchParams(params).toString();
      return await this.get(`/asset-work-records?${queryString}`);
    }
    return await this.get('/asset-work-records');
  }

  // ✅ THÊM MỚI: Lấy work records theo asset ID cụ thể
  async getAssetWorkRecordsByAssetId(assetId: number) {
    return await this.get(`/asset-work-records/asset/${assetId}`);
  }

  async getAssetWorkRecordById(id: number) {
    return await this.get(`/asset-work-records/${id}`);
  }

  async createAssetWorkRecord(data: any) {
    return await this.post('/asset-work-records', data);
  }

  async updateAssetWorkRecord(id: number, data: any) {
    return await this.put(`/asset-work-records/${id}`, data);
  }

  async deleteAssetWorkRecord(id: number) {
    return await this.delete(`/asset-work-records/${id}`);
  }

  // Asset Work Records workflow methods
  async approveWorkRecordExecution(id: number, data: any) {
    return await this.post(`/asset-work-records/${id}/approve-execution`, data);
  }

  async startWorkRecord(id: number, data: any) {
    return await this.post(`/asset-work-records/${id}/start-work`, data);
  }

  async completeWorkRecord(id: number, data: any) {
    return await this.post(`/asset-work-records/${id}/complete`, data);
  }

  async approveWorkRecordCompletion(id: number, data: any) {
    return await this.post(`/asset-work-records/${id}/approve-completion`, data);
  }

  // Maintenance Campaign methods
  async getMaintenanceCampaigns(params?: any) {
    return await this.get('/maintenance-campaigns', params);
  }

  async getMaintenanceCampaignById(id: number) {
    return await this.get(`/maintenance-campaigns/${id}`);
  }

  async getMaintenanceCampaignWorkRecords(id: number) {
    return await this.get(`/maintenance-campaigns/${id}/work-records`);
  }

  async exportMaintenanceCampaignExcel(id: number): Promise<Blob> {
    const token = localStorage.getItem('authToken');
    const url = `${this.client.defaults.baseURL?.replace('/api', '') || ''}/api/maintenance-campaigns/${id}/export-excel`;
    const resp = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!resp.ok) throw new Error('Không thể xuất Excel');
    return await resp.blob();
  }

  async createMaintenanceCampaign(data: any) {
    return await this.post('/maintenance-campaigns', data);
  }

  async updateMaintenanceCampaign(id: number, data: any) {
    return await this.put(`/maintenance-campaigns/${id}`, data);
  }

  async deleteMaintenanceCampaign(id: number) {
    return await this.delete(`/maintenance-campaigns/${id}`);
  }

  // ✅ CẢI TIẾN: Thêm các method API mới
  async getCampaignCompletionStatus(campaignId: number) {
    return await this.get(`/maintenance-campaigns/${campaignId}/completion-status`);
  }

  async getPendingWorkRecords(campaignId: number) {
    return await this.get(`/maintenance-campaigns/${campaignId}/pending-work-records`);
  }

  async checkCampaignConsistency(campaignId: number) {
    return await this.get(`/maintenance-campaigns/${campaignId}/check-consistency`);
  }

  async syncWorkRecordsStatus(campaignId: number) {
    return await this.post(`/maintenance-campaigns/${campaignId}/sync-work-records`);
  }

  // Maintenance Campaign workflow methods
  async startMaintenanceCampaign(id: number) {
    return await this.post(`/maintenance-campaigns/${id}/start`);
  }

  async submitMaintenanceCampaignForApproval(id: number) {
    return await this.post(`/maintenance-campaigns/${id}/submit-for-approval`);
  }

  async approveMaintenanceCampaign(id: number, data: any) {
    return await this.post(`/maintenance-campaigns/${id}/approve`, data);
  }

  async approveMaintenanceCampaignExecution(id: number, data?: any) {
    return await this.post(`/maintenance-campaigns/${id}/approve-execution`, data);
  }

  async approveMaintenanceCampaignCompletion(id: number, data: any) {
    return await this.post(`/maintenance-campaigns/${id}/approve-completion`, data);
  }

  // Asset Work Advanced methods
  async createScheduledCampaigns() {
    return await this.post('/asset-work-records/advanced/create-scheduled-campaigns');
  }

  async getAssetsByLocationAndMonth(locationId: number, month: number, year: number) {
    return await this.get('/assets/by-location-month', { location_id: locationId, month, year });
  }

  async getAssetsByLocationAndDateRange(locationId: number, startDate: string, endDate: string) {
    return await this.get('/assets/by-location-date-range', { location_id: locationId, start_date: startDate, end_date: endDate });
  }

  async getAssetsDueForMaintenance(month?: number, year?: number, companyId?: number) {
    return await this.get('/assets/due-for-maintenance', { month, year, company_id: companyId });
  }

  async getUpcomingWorkRecords(days: number = 7) {
    return await this.get('/asset-work-records/advanced/upcoming', { days });
  }

  async getWorkStatistics(startDate: string, endDate: string) {
    return await this.get('/asset-work-records/advanced/statistics', { start_date: startDate, end_date: endDate });
  }

  async createEmergencyWorkRecord(data: any) {
    return await this.post('/asset-work-records/advanced/emergency', data);
  }

  async getAssetWorkReport(assetId: number) {
    return await this.get(`/asset-work-records/advanced/report/${assetId}`);
  }

  // Legacy method for backward compatibility
  async getAssetMaintenances(assetId: number) {
    // ✅ SỬA: Gọi API đúng endpoint để lấy work records theo asset ID cụ thể
    return await this.getAssetWorkRecordsByAssetId(assetId);
  }

  // Maintenance Type methods
  async getMaintenanceTypes() {
    return await this.get('/maintenance-types');
  }
  async getMaintenanceType(id: number) {
    return await this.get(`/maintenance-types/${id}`);
  }
  async createMaintenanceType(data: any) {
    return await this.post('/maintenance-types', data);
  }
  async updateMaintenanceType(id: number, data: any) {
    return await this.put(`/maintenance-types/${id}`, data);
  }
  async deleteMaintenanceType(id: number) {
    return await this.delete(`/maintenance-types/${id}`);
  }

  // Company methods
  async getCompanies() {
    return await this.get('/companies');
  }
  async getCompany(id: number) {
    return await this.get(`/companies/${id}`);
  }
  async createCompany(data: any) {
    return await this.post('/companies', data);
  }
  async updateCompany(id: number, data: any) {
    return await this.put(`/companies/${id}`, data);
  }
  async deleteCompany(id: number) {
    return await this.delete(`/companies/${id}`);
  }

  // Company statistics methods
  async getCompanyStats() {
    return await this.get('/companies/stats/summary');
  }
  async getCompanyAssetStats() {
    return await this.get('/companies/stats/assets');
  }
  async getCompanyDisposedStats() {
    return await this.get('/companies/stats/disposed');
  }

  // Supplier methods
  async getSuppliers() {
    return await this.get('/suppliers');
  }
  async getSupplier(id: number) {
    return await this.get(`/suppliers/${id}`);
  }
  async createSupplier(data: any) {
    return await this.post('/suppliers', data);
  }
  async updateSupplier(id: number, data: any) {
    return await this.put(`/suppliers/${id}`, data);
  }
  async deleteSupplier(id: number) {
    return await this.delete(`/suppliers/${id}`);
  }

  // Employee methods
  async getEmployees(params?: any) {
    if (params && typeof params === 'object') {
      const queryString = new URLSearchParams(params).toString();
      return await this.get(`/employees?${queryString}`);
    }
    return await this.get('/employees');
  }

  async getEmployee(id: number) {
    return await this.get(`/employees/${id}`);
  }

  async createEmployee(data: any) {
    return await this.post('/employees', data);
  }

  async updateEmployee(id: number, data: any) {
    return await this.put(`/employees/${id}`, data);
  }

  async deleteEmployee(id: number) {
    return await this.delete(`/employees/${id}`);
  }

  // Thêm hàm importEmployees
  async importEmployees(data: any[]) {
    return await this.post('/employees/import', { employees: data });
  }

  // Asset Category methods
  async getAssetCategories() {
    return await this.get('/asset-categories');
  }

  async getAssetCategory(id: number) {
    return await this.get(`/asset-categories/${id}`);
  }

  async createAssetCategory(categoryData: any) {
    return await this.post('/asset-categories', categoryData);
  }

  async updateAssetCategory(id: number, categoryData: any) {
    return await this.put(`/asset-categories/${id}`, categoryData);
  }

  async deleteAssetCategory(id: number) {
    return await this.delete(`/asset-categories/${id}`);
  }

  // Location methods
  async getLocations(params?: any) {
    return await this.get('/locations', params);
  }
  async getLocation(id: number) {
    return await this.get(`/locations/${id}`);
  }
  async createLocation(data: any) {
    return await this.post('/locations', data);
  }
  async updateLocation(id: number, data: any) {
    return await this.put(`/locations/${id}`, data);
  }
  async deleteLocation(id: number) {
    return await this.delete(`/locations/${id}`);
  }

  async getLocationsByCompany(company_id: number) {
    return await this.get('/locations', { company_id });
  }

  // User methods
  async getUsers() {
    return await this.get('/users');
  }
  async getUser(id: number) {
    return await this.get(`/users/${id}`);
  }
  async createUser(data: any) {
    return await this.post('/users', data);
  }
  async updateUser(id: number, data: any) {
    return await this.put(`/users/${id}`, data);
  }
  async deleteUser(id: number) {
    return await this.delete(`/users/${id}`);
  }

  async changeUserPassword(userId: number, oldPassword: string, newPassword: string) {
    return await this.post(`/users/${userId}/change-password`, { oldPassword, newPassword });
  }

  async deleteAssetAttachment(id: number) {
    return await this.delete(`/asset-attachments/${id}`);
  }

  // SEARCH METHODS TOÀN CỤC
  async searchAssets(query: string) {
    return await this.get('/assets', { search: query, limit: 10 });
  }
  async searchEmployees(query: string) {
    return await this.get('/employees', { search: query, limit: 10 });
  }
  async searchSuppliers(query: string) {
    return await this.get('/suppliers', { search: query, limit: 10 });
  }
  async searchLocations(query: string) {
    return await this.get('/locations', { search: query, limit: 10 });
  }

  // Asset Accessories methods
  async getAssetAccessories(assetId: number): Promise<Accessory[]> {
    return await this.get(`/assets/${assetId}/accessories`);
  }
  async getAssetAccessory(id: number) {
    return await this.get(`/accessories/${id}`);
  }
  async createAssetAccessory(assetId: number, data: any) {
    return await this.post(`/assets/${assetId}/accessories`, data);
  }
  async updateAssetAccessory(id: number, data: any) {
    return await this.put(`/accessories/${id}`, data);
  }
  async deleteAssetAccessory(id: number) {
    return await this.delete(`/accessories/${id}`);
  }

  // Kiểm kê nhanh tài sản
  async quickAuditAsset(asset_id: number, data: QuickAuditData) {
    return await this.post('/inventory-audits/quick-audit', { asset_id, ...data });
  }

  async getAssetAuditHistory(assetId: number) {
    return await this.get(`/inventory-audit-details`, { asset_id: assetId });
  }

  // Inventory Audit methods
  async getInventoryAudits() {
    return await this.get('/inventory-audit');
  }

  async getInventoryAuditById(id: number) {
    return await this.get(`/inventory-audit/${id}/details`);
  }

  async getInventoryAuditDetails(auditId: number) {
    return await this.get(`/inventory-audit/${auditId}/details`);
  }

  async updateInventoryAuditDetail(auditId: number, assetId: number, data: any) {
    return await this.put(`/inventory-audit/${auditId}/details/${assetId}`, data);
  }

  async createInventoryAudit(data: any) {
    return await this.post('/inventory-audit', data);
  }

  // Campaign Audit methods
  async createCampaignAudit(data: any) {
    return await this.post('/inventory-audit/auto-load', data);
  }

  async loadAssetsForAudit(data: any) {
    return await this.post('/inventory-audit/load-assets', data);
  }

  async getAuditDetailForAsset(auditId: number, assetId: number) {
    return await this.get(`/inventory-audit/${auditId}/audit-details/${assetId}`);
  }

  async updateAuditDetail(auditId: number, assetId: number, data: any) {
    return await this.put(`/inventory-audit/${auditId}/audit-details/${assetId}`, data);
  }

  async getPendingAuditDetails(auditId: number) {
    return await this.get(`/inventory-audit/${auditId}/pending-details`);
  }

  async getCompletedAuditDetails(auditId: number) {
    return await this.get(`/inventory-audit/${auditId}/completed-details`);
  }

  async approveAudit(auditId: number, data: any) {
    return await this.post(`/inventory-audit/${auditId}/approve`, data);
  }

  async approveAuditDetail(detailId: number, auditDocumentCode: string) {
    return await this.post(`/inventory-audit-details/${detailId}/approve`, { audit_document_code: auditDocumentCode });
  }

  async approveExecution(auditId: number, executionDocumentCode: string) {
    return await this.post(`/inventory-audit/${auditId}/approve-execution`, { executionDocumentCode });
  }

  async completeCampaignAudit(auditId: number, completionDocumentCode: string) {
    return await this.post(`/inventory-audit/${auditId}/complete-campaign`, { completionDocumentCode });
  }

  async getAuditSummary(auditId: number) {
    return await this.get(`/inventory-audit/${auditId}/summary`);
  }

  async addAssetToAuditDetail(data: any) {
    return await this.post('/inventory-audit/add-assets', data);
  }

  async deleteInventoryAuditDetail(detailId: number) {
    return await this.delete(`/inventory-audit/details/${detailId}`);
  }

  async deleteInventoryAudit(auditId: number) {
    return await this.delete(`/inventory-audit/${auditId}`);
  }

  // System Audit Log methods
  async getSystemAuditLogs(params?: {
    user_id?: number;
    action_type?: string;
    target_entity_type?: string;
    target_entity_id?: number;
    start_date?: string;
    end_date?: string;
    ip_address?: string;
    limit?: number;
    offset?: number;
  }) {
    return await this.get('/system-audit-log', params);
  }

  async getSystemAuditLogById(id: number) {
    return await this.get(`/system-audit-log/${id}`);
  }

  async getSystemAuditStatistics() {
    return await this.get('/system-audit-log/statistics/overview');
  }

  async exportSystemAuditLogs(format: 'csv' | 'json', params?: any) {
    const response = await this.client.get('/system-audit-log/export/data', {
      params: { ...params, format },
      responseType: 'blob'
    });
    return response.data;
  }

  async cleanupOldAuditLogs(daysToKeep: number = 365) {
    return await this.post('/system-audit-log/cleanup/old-logs', { daysToKeep });
  }

  // Asset Transfer methods
  async getAssetTransfers(params?: {
    page?: number;
    limit?: number;
    status?: string;
    from_employee_id?: number;
    to_employee_id?: number;
    search?: string;
  }) {
    return await this.get('/asset-transfers', params);
  }

  async getAssetTransferById(id: number) {
    return await this.get(`/asset-transfers/${id}`);
  }

  async createAssetTransfer(data: {
    company_id?: number;
    from_employee_id: number;
    to_employee_id: number;
    reason?: string;
    asset_ids: number[];
  }) {
    return await this.post('/asset-transfers', data);
  }

  async updateAssetTransfer(id: number, data: {
    reason?: string;
    status?: string;
    approval_document_code?: string;
  }) {
    return await this.put(`/asset-transfers/${id}`, data);
  }

  async deleteAssetTransfer(id: number) {
    return await this.delete(`/asset-transfers/${id}`);
  }

  async approveAssetTransfer(id: number, data: { approval_document_code: string }) {
    return await this.post(`/asset-transfers/${id}/approve`, data);
  }





  async getEmployeeAssets(employeeId: number) {
    return await this.get(`/asset-transfers/employee/${employeeId}/assets`);
  }

  async getAssetTransferStats() {
    return await this.get('/asset-transfers/stats');
  }

  // Disposal API methods
  async getDisposalRequests(params?: {
    page?: number;
    limit?: number;
    status?: string;
    requested_by?: number;
    date_from?: string;
    date_to?: string;
    search?: string;
  }) {
    return await this.get('/disposal-requests', params);
  }

  async getDisposalRequestById(id: number) {
    return await this.get(`/disposal-requests/${id}`);
  }

  async createDisposalRequest(data: {
    title: string;
    reason: string;
    asset_ids: number[];
    company_id?: number;
  }) {
    return await this.post('/disposal-requests', data);
  }

  async updateDisposalRequest(id: number, data: {
    title?: string;
    reason?: string;
    status?: string;
  }) {
    return await this.put(`/disposal-requests/${id}`, data);
  }

  async deleteDisposalRequest(id: number) {
    return await this.delete(`/disposal-requests/${id}`);
  }

  async approveDisposalRequest(id: number, data: { approval_document_code: string }) {
    return await this.put(`/disposal-requests/${id}/approve`, data);
  }

  async rejectDisposalRequest(id: number, data: { rejection_notes?: string }) {
    return await this.put(`/disposal-requests/${id}/reject`, data);
  }

  async getDisposedAssets(params?: {
    page?: number;
    limit?: number;
    company_id?: number;
    employee_id?: number;
    location_id?: number;
    date_from?: string;
    date_to?: string;
    search?: string;
  }) {
    return await this.get('/assets/disposed', params);
  }

  async getDisposalStats() {
    return await this.get('/disposal-requests/stats');
  }

  // Recreate asset from a disposed one
  async recreateAssetFromDisposed(data: {
    source_asset_id: number;
    target_company_id: number;
    new_asset_code: string;
    assigned_to_employee_id?: number;
    location_id?: number;
    copy_accessories?: boolean;
  }) {
    return await this.post('/assets/recreate-from-disposed', data);
  }
}

const apiClient = new ApiClient();
export default apiClient;
// Export các hàm search cho GlobalSearch
export const searchAssets = (query: string) => apiClient.searchAssets(query);
export const searchEmployees = (query: string) => apiClient.searchEmployees(query);
export const searchSuppliers = (query: string) => apiClient.searchSuppliers(query);
export const searchLocations = (query: string) => apiClient.searchLocations(query);

export async function getSoonDueMaintenances(days = 7) {
  return await apiClient.get('/asset-maintenance-schedules/soon-due', { days });
}

export async function getSoonAuditAssets(days = 7) {
  return await apiClient.get('/assets/soon-audit', { days });
}

export async function getSoonDueMaintenances30() {
  return await apiClient.get('/asset-maintenance-schedules/soon-due-30');
}

export async function getSoonDueMaintenances7() {
  return await apiClient.get('/asset-maintenance-schedules/soon-due-7');
}

export async function getOverdueMaintenances() {
  return await apiClient.get('/asset-maintenance-schedules/overdue');
}

export async function getOverdueAuditAssets() {
  return await apiClient.get('/assets/overdue-audit');
}

export async function getSoonAuditAssets7() {
  return await apiClient.get('/assets/soon-audit-7');
}

export async function getSoonAuditAssets30() {
  return await apiClient.get('/assets/soon-audit-30');
}

export async function getAssetSummary() {
  return await apiClient.get('/assets/summary');
}

export async function getAssetStatusByCompany() {
  return await apiClient.get('/assets/status-by-company');
}

export async function getCompanyStats() {
  return await apiClient.getCompanyStats();
}

export async function getCompanyAssetStats() {
  return await apiClient.getCompanyAssetStats();
}

export async function getCompanyDisposedStats() {
  return await apiClient.getCompanyDisposedStats();
}

export interface Accessory {
  id: number;
  name: string;
  quantity: number;
  unit?: string;
  price?: number;
  total_price?: number;
  purchase_date?: string;
  purchase_document?: string;
  notes?: string;
} 